import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';

import Banner from '../../../components/BannerDefault';
import Depoimentos from '../../../components/Carousel/CarouselDepoimentos';
import MetaTags from '../../../components/MetaTags';
import {
  payloadContatos,
  payloadDepoimentos,
  payloadEmpreendimentos,
  payloadEmpreendimentosNotSlug,
  payloadEmpreendimentosSlugs,
  payloadImagens,
  payloadListas,
  payloadSedes,
  payloadTextos,
} from '../../../gql/empreendimentos';
import { faseObraMap } from '../../../interfaces';
import ConvertDate from '../../../utils/convertDate';
import { customLoader } from '../../../utils/customLoaderImage';
import { findImage, imagemSizes } from '../../../utils/ImageFindSizes';
import { getCollectionCMSGraphQL } from '../../api/payloadCMS';

import Cadastro from './cadastro';
import Conceito from './conceito';
import Diferenciais from './diferenciais';
import Frasevendedora from './fraseVendedora';
import Galeria from './galeria';
import Localizacao from './localizacao';
import Sugestoes from './sugestoes';
import Vr360 from './vr360';

export default function Index({
  empreendimentos, empreendimento, imoveisDisponiveis, depoimentos,
}) {
  const router = useRouter();
  const rota = `https://hypeempreendimentos.com.br${router.asPath}`;

  if (!empreendimento) return null;
  const convertDate = new ConvertDate();
  const nomeEmpreendimento = empreendimento?.empreendimento;
  const banner = imagemSizes(findImage(empreendimento?.imagens, 'banner-principal'));
  const logo = imagemSizes(findImage(empreendimento?.imagens, 'logo-empreendimento'));
  const conceito = empreendimento?.textos?.find((txt: any) => txt?.referencia === 'conceito')?.texto?.conteudo;
  const valorMinimo = empreendimento?.valor_minimo;
  const valorMaximo = empreendimento?.valor_maximo;
  const faseObra = empreendimento?.fase_obra;
  const informacoesImagem = empreendimento?.imagens?.find((img: any) => img.referencia === 'imagem-informacoes')?.imagem;
  const informacoesIcones = empreendimento?.imagens
    ?.filter((img: any) => img.referencia.includes('icone-informacoes'))
    .map((img: any) => img?.imagem?.url);
  const informacoesItens = [
    empreendimento?.tipologia,
    empreendimento?.area_privativa,
    empreendimento?.garagem,
    empreendimento?.area_comum,
    process.env.FEATURE_FLAG_DATA_ENTREGA ? `Entrega em ${convertDate.parseDateToMonthYearText(empreendimento?.entrega)}` : '',
  ];
  const localizacao = empreendimento?.textos?.find((txt: any) => txt?.referencia === 'localizacao')?.texto?.conteudo;
  const latitude = empreendimento?.latitude;
  const longitude = empreendimento?.longitude;
  const destaque = empreendimento?.textos?.find((txt: any) => txt?.referencia === 'destaque')?.texto?.conteudo;
  const vr360 = empreendimento?.textos?.find((txt: any) => txt?.referencia === 'vr360')?.texto ?? null;
  const galeria = empreendimento?.imagens.filter((img: any) => img?.referencia?.includes('galeria'));
  const plantas = empreendimento?.imagens?.filter((img: any) => img?.referencia?.includes('planta'));
  const obra = empreendimento?.imagens?.filter((img: any) => img?.referencia?.includes('obra'));

  return (
    <>
      <Head>
        <MetaTags
          data={{
            titulo: empreendimento?.seo?.seo?.metaTitle,
            descricao: empreendimento?.seo?.seo?.metaDescription,
            imagem: empreendimento?.seo?.seo?.ogImage?.url,
            rota,
          }}
        />
      </Head>
      <Banner
        image={{
          src: banner || '',
        }}
        width='100%'
        height='70vh'>
        <figure>
          <Image
            objectFit='contain'
            width='280px'
            height='280px'
            src={logo || ''}
            loader={customLoader}
            alt='Logo do Empreendimento'
            priority
          />
          <figcaption>{empreendimento?.empreendimento}</figcaption>
        </figure>
      </Banner>
      <Conceito
        conceito={conceito}
        valorMinimo={valorMinimo}
        valorMaximo={valorMaximo}
        faseObra={faseObra}
      />
      <Diferenciais data={{ informacoesImagem, informacoesIcones, informacoesItens }} />
      <Localizacao data={{ localizacao, latitude, longitude }} />
      <Frasevendedora destaque={destaque} />
      {vr360 && (
        <Vr360
          url={vr360.conteudo}
          empreendimento={nomeEmpreendimento}
        />
      )}
      <Galeria data={{ galeria, plantas, obra }} />
      {depoimentos && <Depoimentos depoimentos={depoimentos} />}
      <Cadastro />
      <Sugestoes
        currentProduct={empreendimentos}
        disponibilidade={imoveisDisponiveis}
      />
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async() => {
  const getEmpreendimentosCMS: any = await getCollectionCMSGraphQL(
    'Empreendimentos',
    payloadEmpreendimentos({
      limit: 100,
    }),
  );
  const cmsEmpreendimentos = getEmpreendimentosCMS?.filter((emp: any) => emp?.visualizacao === 'venda');
  const empreendimentosCMSSlugs = cmsEmpreendimentos?.map((emp: any) => emp?.slug);

  return {
    paths: empreendimentosCMSSlugs?.map((slug: any) => ({ params: { slug } })),
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async({ params }) => {
  const { slug } = params;

  const getEmpreendimentosCMS: any = await getCollectionCMSGraphQL(
    'Empreendimentos',
    payloadEmpreendimentosSlugs({
      limit: 1,
      slug,
    }),
  );
  const empreendimentoCMSData = getEmpreendimentosCMS?.find((emp: any) => emp?.slug === slug);

  const imoveisDisponiveis = getEmpreendimentosCMS
    ?.filter((emp: any) => emp?.visualizacao === 'venda' && emp?.slug !== slug)
    ?.map((empHype: any) => ({
      id: empHype?.id,
      visualizacao: empHype?.visualizacao,
      foto: {
        url: empHype?.imagens?.find((img: any) => img?.referencia === 'banner-principal')?.imagem?.url,
      },
      thumbnail: empHype?.imagens?.find((img: any) => img?.referencia === 'thumbnail')?.imagem?.url,
      cidade: empHype?.cidade,
      bairro: empHype?.bairro,
      variacaoTipologia: empHype?.tipologia,
      areaComum: empHype?.area_comum,
      numeroDeDormitorios: empHype?.tipologia,
      dataDeEntrega: empHype?.entrega,
      numeroDeGaragensDescricao: empHype?.garagem,
      numeroDeGaragens: [empHype?.numero_de_garagens || 0],
      numeroDeGaragensMax: empHype?.numero_de_garagens_max || 0,
      slug: empHype?.slug,
      faseObra: faseObraMap?.[empHype?.fase_obra],
      valorMaximo: empHype?.valor_maximo,
      valorMinimo: empHype?.valor_minimo,
      metragemMinima: empHype?.area_privativa,
      metragemMaxima: empHype?.area_privativa,
      nome: empHype?.empreendimento,
      criadoEm: empHype?.createdAt,
    }));

  const depoimentos: any = await getCollectionCMSGraphQL(
    'Depoimentos',
    payloadDepoimentos({
      limit: 100,
    }),
  );

  const empreendimento = {
    ...empreendimentoCMSData,
  };

  const imagens: any = await getCollectionCMSGraphQL(
    'Imagens',
    payloadImagens({
      limit: 6,
      titulo: ['logo', 'facebook_icon', 'instagram_icon', 'youtube_icon', 'linkedin_icon', 'logo_escura'],
    }),
  );
  const logo = imagens?.find((img: any) => img?.titulo === 'logo')?.url;

  const footerIcons = imagens?.filter((img: any) => ['facebook_icon', 'instagram_icon', 'youtube_icon', 'linkedin_icon', 'logo_escura'].includes(img?.titulo),
  );

  const listas: any = await getCollectionCMSGraphQL(
    'Listas',
    payloadListas({
      limit: 1,
      tituloLista: 'Site - header',
    }),
  );
  const routes = listas?.find((list: any) => list?.itens)?.itens || [];

  const getSedes: any = await getCollectionCMSGraphQL(
    'Sedes',
    payloadSedes({
      limit: 2,
      nomes: ['Sede 1', 'Americana'],
    }),
  );
  const address = getSedes?.filter((sede: any) => ['Sede 1', 'Americana'].includes(sede?.nome));

  const socialMedia = getSedes
    ?.filter((sede) => ['Sede 1'].includes(sede?.nome))
    .map(({
      instagram, youtube, facebook, linkedin,
    }) => ({
      instagram,
      youtube,
      facebook,
      linkedin,
    }))[0];

  const getContatos: any = await getCollectionCMSGraphQL(
    'Contatos',
    payloadContatos({
      limit: 1,
      tituloLista: 'Geral',
    }),
  );
  const contact = getContatos?.filter((list) => ['Geral'].includes(list.tituloLista))?.[0]?.contatos || { contatos: [] };

  const textos: any = await getCollectionCMSGraphQL(
    'Textos',
    payloadTextos({
      limit: 1,
      titulo: 'Copyright',
    }),
  );
  const copy = textos?.find((txt: any) => txt?.titulo === 'Copyright')?.conteudo;

  const maisEmpreedimentos: any = await getCollectionCMSGraphQL(
    'Empreendimentos',
    payloadEmpreendimentosNotSlug({
      slug,
      limit: 6,
    }),
  );

  const empreendimentos: any = maisEmpreedimentos?.map((empHype: any) => ({
    id: empHype?.id,
    visualizacao: empHype?.visualizacao,
    foto: {
      url: empHype?.imagens?.find((img: any) => img?.referencia === 'banner-principal')?.imagem?.url,
    },
    thumbnail: empHype?.imagens?.find((img: any) => img?.referencia === 'thumbnail')?.imagem?.url,
    cidade: empHype?.cidade,
    bairro: empHype?.bairro,
    variacaoTipologia: empHype?.tipologia,
    areaComum: empHype?.area_comum,
    numeroDeDormitorios: empHype?.tipologia,
    dataDeEntrega: empHype?.entrega,
    numeroDeGaragensDescricao: empHype?.garagem,
    numeroDeGaragens: [empHype?.numero_de_garagens || 0],
    numeroDeGaragensMax: empHype?.numero_de_garagens_max || 0,
    slug: empHype?.slug,
    faseObra: faseObraMap?.[empHype?.fase_obra],
    valorMaximo: empHype?.valor_maximo,
    valorMinimo: empHype?.valor_minimo,
    metragemMinima: empHype?.area_privativa,
    metragemMaxima: empHype?.area_privativa,
    nome: empHype?.empreendimento,
    criadoEm: empHype?.createdAt,
  }));

  const headerData = { logo, routes };
  const footerData = {
    routes,
    logo,
    footerIcons,
    address,
    contact,
    copy,
    socialMedia,
  };

  return {
    props: {
      empreendimentos,
      empreendimento,
      imoveisDisponiveis,
      depoimentos,
      headerData,
      footerData,
    },
    revalidate: Number(process.env.REVALIDATE_INTERVAL),
  };
};
